<script lang="ts">
export default {
  name: 'AccountBenchmarking',
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import AccountBenchmarkViewOptions from '@/components/AccountBenchmarkViewOptions.vue';
import ClientFilter from '@/components/ClientFilter.vue';
import { benchmarkService, referenceService } from '@/services/api';
import { Account } from '@/types/Account';
import { AccountBenchmark, CyberBenchmark } from '@/types/Benchmark';
import { AttributeOption, both, calc, Chart, Column, PUBLIC, sameOwnershipType, SHOW_ALL } from './Benchmarking';
import BenchmarkingCharts from './BenchmarkingCharts.vue';
import BenchmarkingTable from './BenchmarkingTable.vue';
import { calcAverage, calcMedian } from '@/utilities/Number';
import GenericFilter from '@/components/GenericFilter.vue';
import { useRoute } from 'vue-router';
import { Benchmark } from '@/types/Benchmark';
import { LabelFilterType } from '@/types/components/FilterType';

const $route = useRoute();

const props = defineProps<{
  account: Account;
}>();

const emit = defineEmits<{
  'needs-update': [];
}>();

// -----
// data
// -----
const isLoading = ref<boolean>(false);
const accountData = ref<CyberBenchmark[]>([]);
const data = ref<CyberBenchmark[]>([]);
const selectedClients = ref<string[]>([]);
const appliedClients = ref<string[]>([]);
const appliedLabelDisplay = ref<string>(SHOW_ALL);
const attributeTypes = ref<AttributeOption[]>([]);
const selectedAttributeTypes = ref<number[]>([]);
const industries = ref<LabelFilterType<number>[]>([]);
const selectedIndustries = ref<number[]>([]);
const accountInfo = ref<AccountBenchmark>({} as AccountBenchmark);
const benchmarkTableKey = ref(0);

// ---------
// computed
// ---------
const clients = computed(() => {
  const clients = data.value.reduce((acc, d) => {
    const isCurrentClient = d.accountId === $route.params.id;
    if (isCurrentClient) {
      return acc;
    }
    const alreadyInAccumulator = acc.some(i => d.accountName && i.text.trim().toLowerCase() === d.accountName.trim().toLowerCase());
    if (alreadyInAccumulator) {
      return acc;
    }
    if (props.account.ownershipType !== d.ownershipType) {
      return acc;
    }
    acc.push({ text: d.accountName || '', value: d.accountName || '' });
    return acc;
  }, [] as { text: string, value: string }[]);
  clients.sort((a, b) => a.text.localeCompare(b.text));
  return clients;
});

const otherData = computed<CyberBenchmark[]>(() => {
  if (appliedClients.value.length > 0) {
    return data.value
      .filter(d => appliedClients.value.includes(d.accountName || ''))
      .filter(d => ('' + d.ownershipType).trim().toLowerCase() === ('' + props.account.ownershipType).trim().toLowerCase())
      .filter(filterByIndustry)
      .map(transformAccountName) as CyberBenchmark[];
  }
  return data.value
    .filter(d => d.accountId !== $route.params.id)
    .filter(d => ('' + d.ownershipType).trim().toLowerCase() === ('' + props.account.ownershipType).trim().toLowerCase())
    .filter(filterByIndustry)
    .map(transformAccountName) as CyberBenchmark[];
});

const averageData = computed<CyberBenchmark[]>(() => {
  return [{
    key: 'avg',
    accountName: ' ',
    policyYear: ' ' as never,
    programType: ' ',
    sic: '',
    industryName: 'Average',
    stockTicker: '',
    ownershipType: '',
    primaryCarrierGroup: '',
    primaryCarrierGroupID: '',
    policyEffectiveDate: '',
    revenueTTM: calc<CyberBenchmark>(otherData.value, 'revenueTTM', calcAverage),
    totalRetention: calc(otherData.value, 'totalRetention', calcAverage, true),
    totalLimit: calc(otherData.value, 'totalLimit', calcAverage),
    totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcAverage),
    maxPrimaryPPM: calc(otherData.value, 'maxPrimaryPPM', calcAverage),
    firstLayerILF: calc(otherData.value, 'firstLayerILF', calcAverage),
    frozen: true,
  }];
});

const medianData = computed<CyberBenchmark[]>(() => {
  return [{
    key: 'med',
    accountName: ' ',
    policyYear: ' ' as never,
    programType: ' ',
    sic: '',
    industryName: 'Median',
    stockTicker: '',
    ownershipType: '',
    primaryCarrierGroup: '',
    primaryCarrierGroupID: '',
    policyEffectiveDate: '',
    revenueTTM: calc(otherData.value, 'revenueTTM', calcMedian),
    totalRetention: calc(otherData.value, 'totalRetention', calcMedian, true),
    totalLimit: calc(otherData.value, 'totalLimit', calcMedian),
    totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcMedian),
    maxPrimaryPPM: calc(otherData.value, 'maxPrimaryPPM', calcMedian),
    firstLayerILF: calc(otherData.value, 'firstLayerILF', calcMedian),
    frozen: true,
  }];
});

const isPublic = computed(() => {
  return props.account.ownershipType?.trim().toLowerCase() === PUBLIC;
});

const isPrivate = computed(() => {
  return !isPublic.value;
});

const titleText = computed(() => {
  return `${isPublic.value ? 'Public' : 'Private'} Cyber Benchmarking`;
});

const columns = computed(() => {
  const columns: (Column<CyberBenchmark> | undefined)[] = [
    { fieldName: 'policyYear', type: 'number', title: 'Year' },
    { fieldName: 'accountName', type: 'nosort', title: 'Client', url: (data) => `/account/${data.accountId}/overview` },
    { fieldName: 'industryName', type: '', title: 'Industry', url: (data) => `/account/${data.accountId}/edit`, accessoryIcon: 'pencil' },
    { fieldName: 'revenueTTM', type: 'compactmoney', title: 'Revenue', classes: isPrivate.value ? 'sorted' : '', filter: 'compactMoney', exposureCategory: 'Company Financials' },
    { fieldName: 'totalLimit', type: 'compactmoney', title: 'Cyber Limits', filter: 'compactMoney', url: (data) => `/account/${data.accountId}/policy-detail`, accessoryIcon: 'pencil' },
    { fieldName: 'totalRetention', type: 'compactmoney', title: 'Retention', filter: 'compactMoney' },
    { fieldName: 'totalPrimaryLimit', type: 'compactmoney', title: 'Primary Limit', filter: 'compactMoney' },
    { fieldName: 'maxPrimaryPPM', type: 'compactmoney', title: 'Primary PPM', filter: 'compactMoney' },
    { fieldName: 'primaryCarrierGroup', type: '', title: 'Primary Carrier', url: (data) => `/carrier/${data.primaryCarrierGroupID}/overview` },
    { fieldName: 'policyEffectiveDate', type: 'date', title: 'Effective Date', filter: 'date', useFilteredExport: true }
  ];
  return columns.filter(x => x) as Column<CyberBenchmark>[];
});

const charts = computed(() => {
  const charts: (Chart<CyberBenchmark> | undefined)[] = [
    { fieldName: 'revenueTTM', title: 'Revenue' },
    { fieldName: 'totalLimit', title: 'Cyber Limits' },
    { fieldName: 'totalRetention', title: 'Retention' },
    { fieldName: 'totalPrimaryLimit', title: 'Primary Limit' },
    { fieldName: 'maxPrimaryPPM', title: 'Primary PPM' },
    { fieldName: 'firstLayerILF', title: '1st XS Cyber Rate', isPercentage: true }
  ];
  return charts.filter(x => x) as Chart<CyberBenchmark>[];
});

const selectedIndustriesSet = computed(() => {
  return selectedIndustries.value.reduce((acc, i) => {
    acc.add(i)
    return acc;
  }, new Set<number>());
});

// ---------
// watchers
// ---------
watch(selectedAttributeTypes, async () => {
  await refresh();
});

watch(otherData, () => {
  // the apptable sorting interferes with reactive table data... this forces a re-render
  benchmarkTableKey.value += 1;
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
  await refresh();
});

// ----------
// functions
// ----------
function transformAccountName(d: CyberBenchmark): CyberBenchmark {
  return ({ ...d, accountName: appliedLabelDisplay.value === SHOW_ALL ? d.accountName : d.accountNameAlt });
}

function applyClientFilter(): void {
  appliedClients.value = selectedClients.value;
}

function applyViewOptions(selectedLabels: string): void {
  appliedLabelDisplay.value = selectedLabels;
}

async function refresh(): Promise<void> {
  isLoading.value = true;
  const [dataResult, attributeTypesResult, account] = await Promise.all([
    benchmarkService.cyber(props.account.ownershipType, selectedAttributeTypes.value),
    referenceService.accountAttributeTypes(),
    benchmarkService.account($route.params.id as string),
  ]);

  accountInfo.value = account;
  attributeTypes.value = attributeTypesResult
      .filter(t => sameOwnershipType(t, props.account.ownershipType) || both(t))
      .map(t => ({ label: t.name, value: t.id }));
  data.value = (dataResult || []).map((d, i) => ({ ...d, accountNameAlt: `Company ${i + 1}` }));

  // upon first load, save off the accountData to populate the first row of the benchmarking table

  if (accountData.value.length === 0) {
    const match = data.value.filter(d => d.accountId === $route.params.id);

    if (match.length > 0) {
      const account = match.reduce((obj, d) => {
        if (!obj.policyYear) return d;
        if (!d.policyYear) return obj;
        if (obj.policyYear < d.policyYear) obj = d;
        return obj;
      }, { ...match[0] });
      accountData.value = [{ ...account, frozen: true }];
    } else {
      accountData.value = [{
        key: 'account',
        accountId: accountInfo.value.accountId,
        accountName: accountInfo.value.accountName,
        sic: accountInfo.value.sic || '',
        stockTicker: accountInfo.value.stockTicker || '',
        ownershipType: accountInfo.value.ownershipType || '',
        revenueTTM: accountInfo.value.annualRevenue,
      }];
    }

    // populate industries filter

    if (industries.value.length === 0) {
      industries.value = Array.from(data.value.reduce((acc, d) => {
        if (d.industryId && d.industryName) {
          acc.set(d.industryId, { label: d.industryName, value: d.industryId });
        }
        return acc;
      }, new Map<number, LabelFilterType<number>>())
        .values())
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  isLoading.value = false;
}

function filterByIndustry(d: CyberBenchmark): boolean {
  if (selectedIndustries.value.length === 0) return true;
  if (!d.industryId) return false;
  return selectedIndustriesSet.value.has(d.industryId);
}
</script>

<template>
  <BenchmarkingTable :titleText="titleText" :key="benchmarkTableKey" :loading="isLoading"
                     :accountData="accountData as Benchmark[]" :otherData="otherData as Benchmark[]"
                     :averageData="averageData as Benchmark[]" :medianData="medianData as Benchmark[]"
                     :data="data" :columns="columns as Column[]"
                     @needs-update="emit('needs-update')"
  >
  <template v-slot:charts>
      <BenchmarkingCharts :accountData="accountData" :otherData="otherData as Benchmark[]" :charts="charts as Chart[]"/>
    </template>
    <template v-slot:filterbar>

      <slot name="filterbar"/>

      <ClientFilter :items="clients"
                    v-model="selectedClients" @update:model-value="applyClientFilter"/>

      <GenericFilter :types="attributeTypes" chip-title-field="label"
                     v-model="selectedAttributeTypes"
                     title="Client Attributes" data-test="client-type-filter"
      />

      <GenericFilter
        title="Industry" chip-title-field="label"
        :types="industries"
        v-model="selectedIndustries"
      />

      <AccountBenchmarkViewOptions :labels="appliedLabelDisplay" @change="applyViewOptions"/>
    </template>
  </BenchmarkingTable>
</template>
